/**
 * Taking the comments out.
 *
 * Two formats, and they are produced in two different places for a reason. CSV
 * is a Worker route — it is a `SELECT` and some quoting. PDF is generated here,
 * in the browser, because a Worker has no renderer and adding a browser binding
 * for a single export is not proportionate. The print dialog is the renderer:
 * an off-screen document, `window.print()`, and the reader chooses "Save as
 * PDF". No dependency, no layout engine to maintain.
 *
 * Both carry the activity implicitly — the resolved state and who is talking —
 * because a status change is part of the record of a review, not chrome.
 */

import { useState } from 'react';
import { api, type ProjectTag, type ThreadWire } from '../api';
import { ds } from '../ds';
import type { Mentionable } from './Composer';
import { activityLine } from './rail/ThreadCard';
import { display, mono, RADIUS } from '../theme';

export function ExportDialog({
  projectId,
  title,
  threads,
  people,
  tags,
  onClose
}: {
  projectId: string;
  title: string;
  threads: ThreadWire[];
  people: Mentionable[];
  tags: ProjectTag[];
  onClose: () => void;
}) {
  const { Button } = ds();
  const [scope, setScope] = useState<'open' | 'all'>('all');

  const chosen = threads.filter((t) => (scope === 'open' ? !t.resolvedAt : true));

  function printable() {
    const nameFor = (key: string) => people.find((p) => p.key === key)?.name ?? 'Someone';
    const tagFor = (id: string | null) => tags.find((t) => t.id === id)?.label ?? '';

    // Grouped by where they are, which is what the design asks for — "one page
    // per lesson" is a grouping in prose, and the page break follows it.
    const groups = new Map<string, ThreadWire[]>();
    for (const t of chosen) {
      const key = t.scope === 'overall' ? 'The course as a whole' : (t.anchorLabel ?? 'Elsewhere');
      groups.set(key, [...(groups.get(key) ?? []), t]);
    }

    const esc = (s: string) =>
      s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    const body = [...groups.entries()]
      .map(
        ([where, list]) => `
          <section>
            <h2>${esc(where)}</h2>
            ${list
              .map(
                (t) => `
              <article>
                <p class="meta">${esc(tagFor(t.tagId))}${
                  t.tagId && t.resolvedAt ? ' · ' : ''
                }${t.resolvedAt ? 'Resolved' : ''}</p>
                ${t.messages
                  .map(
                    (m) =>
                      `<p><strong>${esc(nameFor(m.authorKey))}</strong> — ${esc(m.body)}</p>`
                  )
                  .join('')}
                ${
                  t.activity.length
                    ? `<p class="meta">${t.activity
                        .map((a) => esc(activityLine(a.what, a.actorName)))
                        .join(' · ')}</p>`
                    : ''
                }
              </article>`
              )
              .join('')}
          </section>`
      )
      .join('');

    const html = `<!doctype html><meta charset="utf-8"><title>${esc(title)} — comments</title>
      <style>
        body{font:14px/1.6 system-ui,sans-serif;color:#292b31;margin:40px}
        h1{font-size:26px;margin:0 0 4px}
        .sub{color:#75798c;margin:0 0 28px}
        section{break-before:page;margin-bottom:28px}
        section:first-of-type{break-before:auto}
        h2{font-size:11px;letter-spacing:.14em;text-transform:uppercase;color:#75798c;
           border-bottom:1px solid #e6e4ea;padding-bottom:6px}
        article{margin:14px 0 18px;padding-left:12px;border-left:3px solid #e6e4ea}
        .meta{font-size:10px;letter-spacing:.12em;text-transform:uppercase;color:#75798c;margin:0 0 6px}
        p{margin:0 0 6px}
      </style>
      <h1>${esc(title)}</h1>
      <p class="sub">${chosen.length} comment${chosen.length === 1 ? '' : 's'} · exported ${new Date().toLocaleDateString('en-GB')}</p>
      ${body}`;

    // A hidden iframe rather than a popup: a popup blocker stops the second and
    // the reader never learns why.
    const frame = document.createElement('iframe');
    frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
    document.body.appendChild(frame);
    const doc = frame.contentDocument;
    if (!doc) return;
    doc.open();
    doc.write(html);
    doc.close();
    frame.contentWindow?.focus();
    frame.contentWindow?.print();
    // Left in the DOM until the dialog closes: removing it immediately cancels
    // the print in some browsers.
    setTimeout(() => frame.remove(), 60_000);
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Export comments"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
      className="mv-scrim"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(8,6,16,.6)',
        display: 'grid',
        placeItems: 'center',
        padding: 24,
        zIndex: 60
      }}
    >
      <div
        className="mv-pop"
        style={{
          width: 'min(460px, 100%)',
          background: 'var(--surface-card)',
          borderRadius: RADIUS.panel,
          padding: '28px 30px 24px'
        }}
      >
        <p style={mono}>Export comments</p>
        <h2 style={{ ...display(23), marginTop: 8 }}>Take the feedback with you.</h2>

        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          {(['all', 'open'] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setScope(s)}
              style={{
                ...mono,
                fontSize: 10,
                padding: '6px 10px',
                borderRadius: RADIUS.chip,
                cursor: 'pointer',
                border: `1px solid ${scope === s ? 'var(--primary)' : 'var(--border-subtle)'}`,
                background: scope === s ? 'var(--primary-soft)' : 'transparent',
                color: scope === s ? 'var(--primary)' : 'var(--text-muted)'
              }}
            >
              {s === 'all' ? 'EVERYTHING' : 'OPEN ONLY'}
            </button>
          ))}
        </div>

        <p style={{ color: 'var(--text-muted)', marginTop: 12 }}>
          {chosen.length} comment{chosen.length === 1 ? '' : 's'}, grouped by where they are.
        </p>

        <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              window.location.href = `/api/review/projects/${projectId}/export.csv`;
            }}
          >
            CSV
          </Button>
          <Button onClick={printable}>PDF</Button>
        </div>
        <p style={{ ...mono, marginTop: 10, textAlign: 'right' }}>
          PDF opens your print dialog — choose &ldquo;Save as PDF&rdquo;
        </p>
      </div>
    </div>
  );
}

export function TagEditor({
  projectId,
  tags,
  onClose,
  onSaved
}: {
  projectId: string;
  tags: ProjectTag[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { Button } = ds();
  const [rows, setRows] = useState(tags.map((t) => ({ label: t.label, colour: t.colour })));
  const [busy, setBusy] = useState(false);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Edit tags"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
      className="mv-scrim"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(8,6,16,.6)',
        display: 'grid',
        placeItems: 'center',
        padding: 24,
        zIndex: 60
      }}
    >
      <div
        className="mv-pop"
        style={{
          width: 'min(480px, 100%)',
          background: 'var(--surface-card)',
          borderRadius: RADIUS.panel,
          padding: '28px 30px 24px'
        }}
      >
        <p style={mono}>Tags · editable per course</p>
        <h2 style={{ ...display(23), marginTop: 8 }}>What kinds of comment does this need?</h2>
        <p style={{ color: 'var(--text-body)', marginTop: 10, lineHeight: 1.6 }}>
          The four defaults suit most courses. A compliance course probably wants different
          words from a scripting one.
        </p>

        {rows.map((r, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
            <input
              type="color"
              value={r.colour}
              onChange={(e) =>
                setRows(rows.map((x, j) => (i === j ? { ...x, colour: e.target.value } : x)))
              }
              style={{ width: 32, height: 32, border: 0, background: 'none', padding: 0 }}
            />
            <input
              value={r.label}
              onChange={(e) =>
                setRows(rows.map((x, j) => (i === j ? { ...x, label: e.target.value } : x)))
              }
              style={{
                flex: 1,
                padding: '8px 10px',
                borderRadius: RADIUS.control,
                border: '1px solid var(--border-strong, var(--border-subtle))',
                background: 'var(--surface-sunken, transparent)',
                color: 'var(--text-strong)',
                font: 'inherit'
              }}
            />
            <button type="button" onClick={() => setRows(rows.filter((_, j) => j !== i))}>
              Remove
            </button>
          </div>
        ))}

        {rows.length < 12 && (
          <button
            type="button"
            onClick={() => setRows([...rows, { label: '', colour: '#b2b6ca' }])}
            style={{ ...mono, marginTop: 12, background: 'none', border: 0, color: 'var(--primary)' }}
          >
            + Add a tag
          </button>
        )}

        {/* Said plainly, because it is not obvious and it is not reversible. */}
        <p style={{ ...mono, marginTop: 14, lineHeight: 1.6 }}>
          Removing a tag leaves comments filed under it showing as untagged. It does not
          delete them.
        </p>

        <div style={{ display: 'flex', gap: 10, marginTop: 18, justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await api.tags.replace(
                  projectId,
                  rows.filter((r) => r.label.trim()).map((r) => ({ ...r, label: r.label.trim() }))
                );
                onSaved();
              } finally {
                setBusy(false);
              }
            }}
          >
            Save tags
          </Button>
        </div>
      </div>
    </div>
  );
}
